import {Center, chakra} from '@chakra-ui/react'
import {useLocation} from '@tanstack/react-router'
import {flexRender, Row, Table} from '@tanstack/react-table'
import {useVirtualizer} from '@tanstack/react-virtual'
import {
  Fragment,
  MouseEvent,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {EmptyTableState, EmptyTableStateProps} from '../../../components/ui/empty-table-state.js'
import {Logo} from '../../../components/ui/logo.js'
import {ElementSize, useElementSize} from '../../../hooks/use-element-size.js'
import {BaseRowData} from '../../../types/index.js'
import {getMonthDayFormatter} from '../../../utils/index.js'
import {Anchor, createTopAnchor, toArrayIndex, toBoundArrayIndex, toIndex} from './index.js'
import {InfiniteTableRow} from './infinite-table-row.js'

// const SKELETONS = 50
const ROW_HEIGHT = 48
const HEADER_EXTRA_HEIGHT = 48

function handleRowMouseDown() {
  window.getSelection()?.removeAllRanges()
}

export interface InfiniteDataTableProps<RowData extends BaseRowData> {
  table: Table<RowData>
  anchor: Anchor<RowData>
  /**
   * !!! Avoid setting small page sizes (<100) because it might cause infinite re-renders. !!!
   */
  pageSize: number
  fetchMore: (anchor: Anchor<RowData>) => void
  isFetching: boolean
  canFetchMore: boolean
  /**
   * A string representation of the current filters, used to reset the state of the table.
   */
  filtersHash?: string
  onRowClick?: (data: RowData) => void
  emptyState: EmptyTableStateProps
  hideHeader?: boolean
  hideBorder?: boolean
  rowHeight?: number
}

/**
 * Displays an infinite, virtualized table.
 *
 * !!! For column sizing, use values between 0 and 100, because we're displaying them with relative width. This is probably the most reliable way.
 *
 * !!! When displaying cells, avoid using Chakra components like HStack, VStack, etc., because they make the scrolling laggy. Instead, use like below:
 *
 * ```
 * import { chakra } from '@chakra/react'
 *
 * <chakra.div>
 *  your content
 * </chakra.div>
 * ```
 */
export function InfiniteDataTable<RowData extends BaseRowData>({
  table,
  anchor,
  canFetchMore,
  fetchMore,
  isFetching,
  pageSize: PAGE_SIZE,
  filtersHash,
  onRowClick,
  emptyState,
  hideHeader,
  hideBorder,
  rowHeight = ROW_HEIGHT,
}: InfiniteDataTableProps<RowData>) {
  const {rows} = table.getRowModel()

  const listRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const containerSize = useElementSize(containerRef)
  const location = useLocation()

  const [totalCount, setTotalCount] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)

  const overscan = pageSize / 5
  const nearPageEdgeThreshold = pageSize / 10
  const isLoading = isFetching && rows.length === 0
  const noData = !isFetching && rows.length === 0

  const virtualizer = useVirtualizer({
    count: Math.max(totalCount, rows.length),
    estimateSize: () => rowHeight,
    getScrollElement: () => listRef.current,
    overscan,
  })
  const virtualItems = virtualizer.getVirtualItems()

  const headerCountUpTo = useMemo(() => {
    const counts: number[] = []
    let count = 0
    for (let i = 0; i < rows.length; i++) {
      counts[i] = count
      if (rows[i]?.original?.isHeader) count++
    }
    return counts
  }, [rows])

  // url search is usually used for filters, so we need to reset the state of the table
  useEffect(() => {
    setTotalCount(0)
    listRef.current?.scrollTo({top: 0})
  }, [location.searchStr, filtersHash])

  useEffect(() => {
    const newPageSize = containerSize
      ? Math.max(PAGE_SIZE, Math.ceil(containerSize?.height / rowHeight) * 3)
      : PAGE_SIZE

    setPageSize(prev => {
      if (newPageSize > prev) {
        return newPageSize
      }
      return prev
    })
  }, [PAGE_SIZE, rowHeight, containerSize])

  useEffect(() => {
    if (anchor.direction !== 'forward') return

    const estTotal = anchor.index + rows.length
    setTotalCount(prev => {
      if (estTotal > prev && anchor.startRow) {
        return estTotal
      }
      return prev
    })
  }, [anchor, rows])

  useEffect(() => {
    const [firstItem, lastItem] = [virtualItems[0], virtualItems.at(-1)]
    if (!lastItem || !firstItem) return

    if (anchor.index !== 0 && firstItem.index <= nearPageEdgeThreshold) {
      fetchMore(createTopAnchor<RowData>())
      return
    }

    if (isFetching) return

    const isBackwards = anchor.direction === 'backward'
    const hasPrev = anchor.index !== 0
    const distanceFromStart = isBackwards
      ? firstItem.index - (anchor.index - rows.length)
      : firstItem.index - anchor.index
    const hasNext = isBackwards || rows.length === pageSize
    const distanceFromEnd = isBackwards ? anchor.index - lastItem.index : anchor.index + rows.length - lastItem.index

    if (hasPrev && canFetchMore && distanceFromStart <= nearPageEdgeThreshold) {
      const arrayIndex = toBoundArrayIndex(lastItem.index + nearPageEdgeThreshold * 2, anchor, rows.length)
      const index = toIndex(arrayIndex, anchor) - 1
      const row = rows[arrayIndex]

      fetchMore({
        index,
        direction: 'backward',
        startRow: row?.original || rows[0]?.original || undefined,
      })
      return
    }

    if (hasNext && canFetchMore && distanceFromEnd <= nearPageEdgeThreshold) {
      const arrayIndex = toBoundArrayIndex(firstItem.index - nearPageEdgeThreshold * 2, anchor, rows.length)
      const index = toIndex(arrayIndex, anchor) + 1
      const row = rows[arrayIndex]

      fetchMore({
        index,
        direction: 'forward',
        startRow: row?.original || rows[0]?.original || undefined,
      })
    }
  }, [rows, virtualItems, anchor, isFetching, canFetchMore, fetchMore, pageSize, nearPageEdgeThreshold])

  const handleRowClick = useCallback(
    (event: MouseEvent<HTMLDivElement>, row: Row<RowData>) => {
      if (!onRowClick || !row) return

      const selectedText = window.getSelection()?.toString()
      if (selectedText?.length) {
        event.stopPropagation()
        return
      }

      onRowClick(row.original)
    },
    [onRowClick]
  )

  const handleRowKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>, row: Row<RowData>) => {
      if (!onRowClick || !row) return
      if (event.code === 'Enter' || event.code === 'Space') {
        event.preventDefault()
        onRowClick(row.original)
      }
    },
    [onRowClick]
  )

  return (
    <chakra.div display="flex" flexDirection="column" flex="1 1 0%" height="100%">
      {/* Header */}
      {!hideHeader && !noData && (
        <TableHeader table={table} rows={rows} containerSize={containerSize} listRef={listRef} />
      )}

      {/* Scrollable Container */}
      <chakra.div
        ref={containerRef}
        position="relative"
        overflow="hidden"
        flex="1 1 0%"
        height="100%"
        data-rows-container
      >
        {containerSize && (
          <chakra.div
            ref={listRef}
            overflowY="auto"
            overflowX="hidden"
            position="relative"
            willChange="transform"
            contain="strict"
            style={{
              width: '100%',
              maxWidth: containerSize.width,
              height: containerSize.height,
              overflowY: isLoading ? 'hidden' : 'auto',
            }}
            data-rows-list
          >
            <chakra.div
              position="relative"
              width="100%"
              style={{
                height: isLoading || noData ? '100%' : virtualizer.getTotalSize(),
                maxHeight: isLoading || noData ? '100%' : `${rows.length * rowHeight}px`,
              }}
              rows-virtual-list
              data-e2e="virtual-list"
            >
              {isLoading && (
                <Center width="100%" height="100%" animation="ping">
                  <Logo hideText size={72} />
                </Center>
              )}

              {noData && <EmptyTableState {...emptyState} maxWidth="50%" mx="auto" textAlign="center" />}

              {rows.length > 0 &&
                virtualItems.map(virtualRow => {
                  const arrayIndex = toArrayIndex(virtualRow.index, anchor)
                  const row = rows[arrayIndex]

                  let headerCountBefore = headerCountUpTo[arrayIndex] ?? 0
                  let headerText = ''
                  const headerOffset = virtualRow.start + headerCountBefore * (HEADER_EXTRA_HEIGHT - 1)

                  if (row?.original?.isHeader) {
                    headerCountBefore += 1
                    if ('performedAt' in row.original) {
                      headerText = getMonthDayFormatter().format(row.original?.performedAt as number)
                    }
                  }

                  const offsetY = virtualRow.start + headerCountBefore * HEADER_EXTRA_HEIGHT

                  return (
                    <Fragment key={virtualRow.key}>
                      {row?.original?.isHeader && (
                        <chakra.div
                          display={'flex'}
                          flexDirection={'row'}
                          width={'100%'}
                          alignItems={'center'}
                          position="absolute"
                          top="0"
                          left="0"
                          height="3rem"
                          gap="10px"
                          style={{transform: `translateY(${headerOffset}px)`}}
                        >
                          <chakra.text fontSize={'sm'} whiteSpace={'nowrap'}>
                            {headerText}
                          </chakra.text>
                          <chakra.div
                            width={'100%'}
                            border={'solid'}
                            borderWidth={'1px'}
                            height="0px"
                            borderColor={'border'}
                          ></chakra.div>
                        </chakra.div>
                      )}
                      {row ? (
                        <InfiniteTableRow
                          style={{
                            transform: `translateY(${offsetY}px)`,
                            ...(hideBorder ? {border: 'none'} : {}),
                          }}
                          height={rowHeight}
                          row={row as unknown as Row<BaseRowData> | undefined}
                          selected={row?.getIsSelected()}
                          role="button"
                          onClick={event => handleRowClick(event, row)}
                          onMouseDown={handleRowMouseDown}
                          onKeyDown={event => handleRowKeyDown(event, row)}
                          tabIndex={0}
                          data-url={table.options.meta?.getRowUrl?.(row?.original)}
                        />
                      ) : (
                        <chakra.div
                          position="absolute"
                          top="0"
                          left="0"
                          height={`${rowHeight}px`}
                          width="100%"
                          display="flex"
                          alignItems="center"
                          borderBottomWidth="1px"
                          borderColor="border"
                          style={{transform: `translateY(${offsetY}px)`}}
                          bg="bg.subtle"
                        />
                      )}
                    </Fragment>
                  )
                })}
            </chakra.div>
          </chakra.div>
        )}
      </chakra.div>
    </chakra.div>
  )
}
interface TableHeaderProps<RowData extends BaseRowData> {
  table: Table<RowData>
  containerSize: ElementSize | null
  rows: Row<RowData>[]
  listRef: RefObject<HTMLDivElement | null>
}

function TableHeader<RowData extends BaseRowData>({table, containerSize, rows, listRef}: TableHeaderProps<RowData>) {
  const [scrollbarWidth, setScrollbarWidth] = useState(15)

  // dynamically measure scrollbar width
  useLayoutEffect(() => {
    const rafId = requestAnimationFrame(() => {
      if (listRef.current) {
        const el = listRef.current
        setScrollbarWidth(el.offsetWidth - el.clientWidth)
      }
    })
    return () => cancelAnimationFrame(rafId)
  }, [containerSize, rows, listRef])

  return table.getHeaderGroups().map(headerGroup => (
    <chakra.div
      key={headerGroup.id}
      display="flex"
      borderBottomWidth="1px"
      borderColor="border"
      style={{paddingRight: `${scrollbarWidth}px`}}
    >
      {headerGroup.headers.map(header => (
        <chakra.div
          key={header.id}
          padding="3"
          display="flex"
          alignItems="center"
          fontSize="sm"
          fontWeight="medium"
          color="fg.subtle"
          flex="1 1 0%"
          width="100%"
          style={{
            maxWidth: `${header.getSize()}%`,
            minWidth: header.column.columnDef.minSize ? `${header.column.columnDef.minSize}px` : undefined,
          }}
          data-e2e={header.id}
          className="group"
        >
          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
        </chakra.div>
      ))}
    </chakra.div>
  ))
}
